import React, { useState } from 'react';
import { Auth } from "aws-amplify";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { adminlogin, logout } from "../action/auth";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff, School, CorporateFare, Badge } from '@mui/icons-material';
import AuthLayout from './AuthLayout';

const UserTypeGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '12px',
  marginBottom: '28px',
}));

const TypeCard = styled(Box)(({ theme, selected }) => ({
  padding: '12px',
  borderRadius: '16px',
  border: selected ? '2px solid var(--artemy-accent)' : '1px solid var(--artemy-border)',
  background: selected ? 'rgba(61, 127, 255, 0.08)' : 'var(--artemy-bg-elevated)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: '6px',
  '&:hover': {
    borderColor: selected ? 'var(--artemy-accent)' : 'var(--artemy-border-bright)',
    transform: 'translateY(-2px)',
  }
}));

const IconWrapper = styled(Box)(({ selected }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: selected ? 'var(--artemy-accent)' : 'rgba(139, 150, 180, 0.1)',
  color: selected ? '#fff' : 'var(--artemy-text-secondary)',
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
    '& fieldset': { borderColor: 'var(--artemy-border)' },
    '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)' },
  },
  '& .MuiInputLabel-root': { color: 'var(--artemy-text-dim)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--artemy-accent)' },
  marginBottom: '16px',
});

const ADMIN_TYPES = [
  { id: 'candidate', label: 'Talent Admin', icon: <School fontSize="small" /> },
  { id: 'podprovider', label: 'Partner Admin', icon: <CorporateFare fontSize="small" /> },
  { id: 'employer', label: 'Client Admin', icon: <Badge fontSize="small" /> }
];

const AdminLogin = () => {
  const dispatch = useDispatch();
  const [formState, setFormState] = useState({
    username: '',
    password: '',
    table: '',
    showPassword: false
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formState.table) { setError('Please select an admin type'); return; }
    if (!formState.username) { setError('Please enter an email'); return; }
    if (!formState.password) { setError('Please enter a password'); return; }

    setLoading(true);
    setError(null);

    try {
      await Auth.signIn(formState.username, formState.password);
      const data = await dispatch(adminlogin(formState.username, formState.table));

      if (formState.table === "candidate") {
        if (data[0].type === "CandidateAdmin") {
          // Redirect to candidate admin dashboard when implemented
        } else {
          alert("Invalid Candidate Admin credentials");
          dispatch(logout());
          setLoading(false);
        }
      } else if (formState.table === "podprovider") {
        if (data[0].type === "ProviderAdmin") {
          window.location.href = "/PartnerAdminDashboard";
        } else {
          alert("Invalid Partner Admin credentials");
          dispatch(logout());
          setLoading(false);
        }
      } else {
        if (data[0].type === "ClientAdmin") {
          window.location.href = "/subscriptions";
        } else {
          alert("Invalid Client Admin credentials");
          dispatch(logout());
          setLoading(false);
        }
      }
    } catch (err) {
      setError(err.message || "Authentication failed");
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormState(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const selectAdminType = (type) => {
    setFormState(prev => ({ ...prev, table: type }));
    setError(null);
  };

  return (
    <AuthLayout 
      title="Admin Portal" 
      subtitle="Secure access for system administrators"
    >
      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: '12px', bgcolor: 'rgba(255, 77, 106, 0.1)', color: 'var(--artemy-red)', border: '1px solid rgba(255, 77, 106, 0.2)' }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Typography variant="body2" sx={{ color: 'var(--artemy-text-dim)', mb: 1.5, ml: 0.5, fontWeight: 500 }}>
          Select Admin Role
        </Typography>
        <UserTypeGrid>
          {ADMIN_TYPES.map((type) => (
            <TypeCard 
              key={type.id} 
              selected={formState.table === type.id}
              onClick={() => selectAdminType(type.id)}
            >
              <IconWrapper selected={formState.table === type.id}>
                {type.icon}
              </IconWrapper>
              <Typography variant="caption" sx={{ fontWeight: 600, color: formState.table === type.id ? '#fff' : 'var(--artemy-text-primary)', fontSize: '10px' }}>
                {type.label}
              </Typography>
            </TypeCard>
          ))}
        </UserTypeGrid>

        <StyledTextField
          fullWidth
          label="Admin Email"
          name="username"
          value={formState.username}
          onChange={handleChange}
          required
        />

        <StyledTextField
          fullWidth
          label="Password"
          name="password"
          type={formState.showPassword ? 'text' : 'password'}
          value={formState.password}
          onChange={handleChange}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setFormState(p => ({ ...p, showPassword: !p.showPassword }))} edge="end" sx={{ color: 'var(--artemy-text-dim)' }}>
                  {formState.showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Link to="/forgotpassword" style={{ color: 'var(--artemy-accent)', textDecoration: 'none', fontSize: '13px', fontWeight: 500 }}>
            Forgot password?
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 700,
            bgcolor: 'var(--artemy-accent) !important',
            boxShadow: '0 8px 24px rgba(61, 127, 255, 0.25)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 30px rgba(61, 127, 255, 0.35)',
            }
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Admin Sign In'}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ color: 'var(--artemy-text-secondary)' }}>
            Not an administrator?{' '}
            <Link to="/login" style={{ color: 'var(--artemy-accent)', textDecoration: 'none', fontWeight: 700 }}>
              User Login
            </Link>
          </Typography>
        </Box>
      </form>
    </AuthLayout>
  );
};

export default AdminLogin;